import { Link2, NotebookPen, Sparkles, Timer, Trees } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import type { GardenStats } from "../types";

type Props = {
  stats: GardenStats | null;
  loading?: boolean;
};

// Pixel art is drawn with inline SVG <rect> on a viewBox — crisp, scalable, no assets.
// Each sprite is an array of equal-length strings; chars map to palette colors.
const PALETTE: Record<string, string> = {
  ".": "transparent",
  l: "#84cc16", // lime — young growth
  g: "#16a34a", // green — leaf
  e: "#047857", // emerald — mature
  s: "#7c4a21", // stem / trunk
  d: "#5c3a1a", // dark soil
  o: "#8b6f47", // soil
  f: "#f97316", // flame
  y: "#facc15", // flame core / sun
  p: "#ec4899" // flower accent
};

const SPROUT = [
  ".l.",
  "lgl",
  ".s.",
  ".s."
];

const SMALL = [
  "..l..",
  ".lgl.",
  "llgll",
  "..s..",
  "..s.."
];

const BUSH = [
  ".ggg.",
  "gegeg",
  "ggggg",
  "ggegg",
  "..s..",
  "..s.."
];

const TREE = [
  "..ee..",
  ".eeee.",
  "eeegee",
  "eeeeee",
  ".eege.",
  "..ss..",
  "..ss..",
  "..ss.."
];

const FLOWER = [
  ".p.",
  "pyp",
  ".p.",
  ".g.",
  ".s."
];

const FLAME = [
  "..f..",
  ".fff.",
  "ffyff",
  "fyyyf",
  "ffyff",
  ".fff."
];

type Placement = { sprite: string[]; x: number };

// Baseline (soil top) is y=24 in a 0 0 64 28 viewBox. Each stage adds more/larger plants.
function plotForStage(stage: number): Placement[] {
  switch (stage) {
    case 0:
      return [];
    case 1:
      return [{ sprite: SPROUT, x: 30 }];
    case 2:
      return [
        { sprite: SMALL, x: 14 },
        { sprite: SPROUT, x: 44 }
      ];
    case 3:
      return [
        { sprite: SMALL, x: 8 },
        { sprite: BUSH, x: 28 },
        { sprite: FLOWER, x: 48 }
      ];
    case 4:
      return [
        { sprite: SMALL, x: 4 },
        { sprite: TREE, x: 22 },
        { sprite: BUSH, x: 40 },
        { sprite: FLOWER, x: 54 }
      ];
    default:
      return [
        { sprite: FLOWER, x: 2 },
        { sprite: BUSH, x: 10 },
        { sprite: TREE, x: 24 },
        { sprite: TREE, x: 42 },
        { sprite: SMALL, x: 56 }
      ];
  }
}

function Sprite({ rows, x, baseline }: { rows: string[]; x: number; baseline: number }) {
  const top = baseline - rows.length;
  const cells: ReactElement[] = [];
  rows.forEach((row, r) => {
    row.split("").forEach((ch, col) => {
      const fill = PALETTE[ch];
      if (fill && fill !== "transparent") {
        cells.push(<rect key={`${r}-${col}`} x={x + col} y={top + r} width={1} height={1} fill={fill} />);
      }
    });
  });
  return <g>{cells}</g>;
}

export function GardenStatsPanel({ stats, loading }: Props) {
  const s = stats;
  const stage = s?.stage ?? 0;
  const plants = plotForStage(stage);
  const streakCurrent = s?.streak.current ?? 0;
  const streakLongest = s?.streak.longest ?? 0;
  const total = s?.points.total ?? 0;
  const toNext = s?.nextStageAt != null ? Math.max(0, s.nextStageAt - total) : null;
  const isEmpty = total === 0;

  const stageNames: Record<number, string> = {
    0: "Đất trống",
    1: "Hạt giống",
    2: "Nảy mầm",
    3: "Đang lớn",
    4: "Xum xuê",
    5: "Vườn lâu năm"
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="inline-flex items-center gap-2 text-lg font-bold">
                <Trees size={18} aria-hidden="true" /> Vườn của bạn
              </h1>
              <p className="text-sm font-medium text-muted">
                Vườn lớn dần theo việc bạn học — ghi note, tập trung, kết nối ý.
              </p>
            </div>
            {/* Streak chip with a pixel flame */}
            <div className="flex items-center gap-2 rounded-lg border border-line bg-slate-50 px-3 py-2">
              <svg viewBox="0 0 5 6" width={22} height={26} shapeRendering="crispEdges" aria-hidden="true">
                <Sprite rows={streakCurrent > 0 ? FLAME : FLAME.map((r) => r.replace(/[fy]/g, "."))} x={0} baseline={6} />
                {streakCurrent === 0 ? <rect x={0} y={0} width={5} height={6} fill="#cbd5e1" opacity={0.35} /> : null}
              </svg>
              <div className="leading-tight">
                <p className="text-sm font-bold text-ink">
                  {streakCurrent > 0 ? `${streakCurrent} ngày streak` : "Bắt đầu chuỗi hôm nay"}
                </p>
                <p className="text-[11px] text-muted">kỷ lục {streakLongest} ngày</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4">
          {loading ? <p className="mb-3 text-sm text-muted">Đang tải khu vườn...</p> : null}

          {/* Pixel garden scene */}
          <div className="overflow-hidden rounded-lg border border-line bg-gradient-to-b from-sky-50 to-lime-50">
            <svg
              viewBox="0 0 64 28"
              className="h-48 w-full"
              preserveAspectRatio="xMidYMax meet"
              shapeRendering="crispEdges"
              role="img"
              aria-label={`Khu vườn giai đoạn ${stageNames[stage] ?? stage}`}
            >
              {/* sun appears from stage 3 */}
              {stage >= 3 ? <rect x={54} y={3} width={5} height={5} fill="#facc15" /> : null}
              {/* soil */}
              <rect x={0} y={24} width={64} height={4} fill={PALETTE.o} />
              <rect x={0} y={24} width={64} height={1} fill={PALETTE.d} opacity={0.5} />
              {/* plants */}
              {plants.map((p, i) => (
                <Sprite key={i} rows={p.sprite} x={p.x} baseline={24} />
              ))}
            </svg>
          </div>

          {/* Stage + progress hint */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-700">
              {stageNames[stage] ?? `Giai đoạn ${stage}`}
            </span>
            {isEmpty ? (
              <span className="text-sm text-muted">Khu vườn đang chờ note đầu tiên của bạn 🌱</span>
            ) : toNext != null ? (
              <span className="text-sm text-muted">
                {total} điểm · còn <b className="text-ink">{toNext}</b> điểm tới giai đoạn kế
              </span>
            ) : (
              <span className="text-sm text-muted">{total} điểm · đã đạt giai đoạn cao nhất 🎉</span>
            )}
          </div>

          {/* Stat tiles */}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile icon={<NotebookPen size={15} aria-hidden="true" />} label="Note" value={s?.counts.notes ?? 0} />
            <StatTile icon={<Timer size={15} aria-hidden="true" />} label="Phiên tập trung" value={s?.counts.focusSessions ?? 0} />
            <StatTile icon={<Link2 size={15} aria-hidden="true" />} label="Liên kết" value={s?.counts.connections ?? 0} />
            <StatTile icon={<Sparkles size={15} aria-hidden="true" />} label="Điểm vườn" value={total} />
          </div>
        </div>
      </section>
    </div>
  );
}

function StatTile({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line bg-slate-50 p-3">
      <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        {icon} {label}
      </p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
}
